/**
 * Writing a founder review of an investor.
 *
 * Eligibility is a SERVER question, asked here only to decide whether to offer
 * the action. `investor_startup_have_engaged()` is the same predicate the
 * INSERT policy uses, so the button and the write agree by construction — a
 * founder never sees an action that RLS will then refuse, and hiding the
 * button is not what stops an ineligible write.
 *
 * One review per (investor, startup): a company does not get extra weight for
 * having three co-founders. The unique index enforces it, so a second attempt
 * is an update of the existing row rather than a duplicate.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/lib/supabase/api';
import { isSupabaseConfigured, supabase } from '@/lib/supabase/client';
import { rpc } from '@/lib/supabase/rpc';

/**
 * May the caller's startup review this investor?
 *
 * Both ids are required and are in DIFFERENT spaces: the investor is an
 * auth.users id, the startup a startup_profiles id.
 */
export function useCanReviewInvestor(
  investorUserId: string | undefined,
  startupId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.reviewEligibility(investorUserId ?? '', startupId ?? ''),
    enabled: !!investorUserId && !!startupId && isSupabaseConfigured(),
    queryFn: async (): Promise<boolean> => {
      if (!investorUserId || !startupId) return false;
      const { data, error } = await rpc('investor_startup_have_engaged', {
        p_investor_user_id: investorUserId,
        p_startup_id: startupId,
      });
      if (error) throw error;
      return data === true;
    },
  });
}

export interface SubmitReviewInput {
  investorUserId: string;
  startupId: string;
  authorUserId: string;
  rating: number;
  body: string;
  isAnonymous: boolean;
}

/**
 * Create or revise this startup's review of an investor.
 *
 * Uses upsert on the (investor, startup) identity index so a founder editing
 * their review does not hit a duplicate-key error, and a double-submit does
 * not create two rows — the check-then-insert shape is what produced duplicate
 * directory rows in production on 2026-07-30.
 *
 * `select()` is deliberately NOT chained: `return=representation` applies the
 * SELECT policy to the written row, and the author CAN read their own review,
 * so it would work — but it also serves no purpose here and keeps the write
 * cheaper. The error is checked explicitly because supabase-js resolves with
 * `{ error }` rather than throwing (ARCH-06).
 */
export function useSubmitInvestorReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SubmitReviewInput) => {
      const body = input.body.trim();
      if (!body) throw new Error('Please write a few words before submitting.');
      if (input.rating < 1 || input.rating > 5) {
        throw new Error('Please choose a rating from 1 to 5.');
      }

      const { error } = await supabase.from('investor_founder_reviews').upsert(
        {
          investor_user_id: input.investorUserId,
          startup_id: input.startupId,
          author_user_id: input.authorUserId,
          rating: input.rating,
          body,
          is_anonymous: input.isAnonymous,
        },
        { onConflict: 'investor_user_id,startup_id' }
      );
      if (error) throw error;
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.investorReviews(input.investorUserId),
      });
    },
  });
}
